(() => {
  const localizedStoreUrl = (value) => {
    if (!value || value === '#') return value || '#';
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return value;
    if (document.documentElement.lang === 'nl' && /^\/(collections|products|pages|blogs|search|cart|policies)(?:\/|$)/.test(url.pathname)) url.pathname = '/nl' + url.pathname;
    return url.pathname + url.search + url.hash;
  };
  const alignBrandLockups = () => {
    document.querySelectorAll('[data-brand-lockup]').forEach((lockup) => {
      const wordmark = lockup.querySelector('strong');
      const tagline = lockup.querySelector('small');
      if (!wordmark || !tagline) return;
      tagline.style.removeProperty('--brand-tagline-scale');
      lockup.style.removeProperty('width');
      const wordmarkWidth = wordmark.getBoundingClientRect().width;
      const taglineWidth = tagline.getBoundingClientRect().width;
      if (!wordmarkWidth || !taglineWidth) return;
      lockup.style.width = `${wordmarkWidth}px`;
      tagline.style.setProperty('--brand-tagline-scale', (wordmarkWidth / taglineWidth).toFixed(4));
    });
  };
  const scheduleBrandAlignment = () => window.requestAnimationFrame(alignBrandLockups);
  if (document.fonts?.ready) document.fonts.ready.then(scheduleBrandAlignment);
  else window.addEventListener('load', scheduleBrandAlignment, { once: true });
  window.addEventListener('resize', scheduleBrandAlignment, { passive: true });

  let cartToastTimer;
  const pulseBagIcon = (motion = 'add') => {
    document.querySelectorAll('.header-bag').forEach((bag) => {
      const motionClass = motion === 'remove' ? 'is-cart-reduced' : 'is-cart-bumped';
      bag.classList.remove('is-cart-bumped', 'is-cart-reduced');
      bag.getBoundingClientRect();
      bag.classList.add(motionClass);
      window.setTimeout(() => bag.classList.remove(motionClass), 1350);
    });
  };
  const showCartToast = (title = '', { error = false } = {}) => {
    let toast = document.querySelector('[data-cart-add-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-add-toast';
      toast.dataset.cartAddToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<span aria-hidden="true">✓</span><p><strong>Added to your bag</strong><small></small></p>';
      document.body.append(toast);
    }
    const heading = toast.querySelector('strong');
    const icon = toast.querySelector('span');
    toast.classList.toggle('is-error', error);
    if (heading) heading.textContent = error ? 'This piece was not added' : 'Added to your bag';
    if (icon) icon.textContent = error ? '!' : '✓';
    const copy = toast.querySelector('small');
    if (copy) copy.textContent = title || 'Your selection is ready to review.';
    window.clearTimeout(cartToastTimer);
    toast.classList.remove('is-visible');
    toast.getBoundingClientRect();
    toast.classList.add('is-visible');
    cartToastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3000);
  };

  const menuButton = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-menu]');
  const menuScrim = document.querySelector('[data-menu-scrim]');
  if (menuButton && menu) {
    const setMenuOpen = (open, { restoreFocus = false } = {}) => {
      menu.classList.toggle('is-open', open);
      menuButton.setAttribute('aria-expanded', String(open));
      menuButton.closest('.site-header')?.classList.toggle('is-menu-open', open);
      document.body.classList.toggle('menu-drawer-open', open);
      if (!open && restoreFocus) menuButton.focus();
    };
    menuButton.addEventListener('click', () => {
      const open = !menu.classList.contains('is-open');
      setMenuOpen(open);
    });
    menu.addEventListener('click', (event) => {
      if (event.target.matches('a')) {
        setMenuOpen(false);
      }
    });
    menuScrim?.addEventListener('click', () => setMenuOpen(false, { restoreFocus: true }));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) {
        setMenuOpen(false, { restoreFocus: true });
      }
    });
    document.addEventListener('click', (event) => {
      if (!menu.contains(event.target) && !menuButton.contains(event.target) && !menuScrim?.contains(event.target)) {
        setMenuOpen(false);
      }
    });
  }

  const siteHeader = document.querySelector('[data-header]');
  if (siteHeader) {
    const headerSection = siteHeader.closest('[id^="shopify-section-"]');
    const syncHeaderState = () => {
      const scrolled = window.scrollY > 24;
      siteHeader.classList.toggle('is-scrolled', scrolled);
      headerSection?.classList.toggle('header-section-scrolled', scrolled);
    };
    syncHeaderState();
    window.addEventListener('scroll', syncHeaderState, { passive: true });
  }

  const searchToggle = document.querySelector('[data-search-toggle]');
  const headerSearch = document.querySelector('[data-header-search]');
  const searchClose = headerSearch?.querySelector('[data-search-close]');
  const searchScrim = document.querySelector('[data-search-scrim]');
  const predictiveInput = headerSearch?.querySelector('[data-predictive-input]');
  const predictiveResults = headerSearch?.querySelector('[data-predictive-results]');
  const searchSummary = headerSearch?.querySelector('[data-search-summary]');
  const searchAll = headerSearch?.querySelector('[data-search-all]');
  if (searchToggle && headerSearch && predictiveInput && predictiveResults) {
    let searchTimer;
    let searchRequest;
    let searchCatalog = [];
    try { searchCatalog = JSON.parse(headerSearch.querySelector('[data-search-catalog]')?.textContent || '[]'); } catch (error) { searchCatalog = []; }

    const formatSearchPrice = (value) => {
      const rawValue = String(value ?? '').trim();
      if (!rawValue || /[€$£¥]/.test(rawValue)) return rawValue;
      const amount = Number(rawValue.replace(',', '.'));
      if (!Number.isFinite(amount)) return rawValue;
      const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'EUR';
      const locale = currency === 'EUR' ? 'nl-NL' : (document.documentElement.lang || 'en');
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount).replace(/^([^\d-]+)\s+/, '$1');
      } catch (error) {
        return rawValue;
      }
    };

    const renderSearchProducts = (products, label) => {
      predictiveResults.replaceChildren();
      if (searchSummary) searchSummary.textContent = label;
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'predictive-empty';
        empty.textContent = predictiveInput.value.trim() ? 'No matching pieces yet. Try a colour, collection or object name.' : 'Begin typing to explore the collection.';
        predictiveResults.append(empty);
        return;
      }
      products.slice(0, 6).forEach((product) => {
        const card = document.createElement('article');
        card.className = 'predictive-product';
        const link = document.createElement('a');
        link.href = localizedStoreUrl(product.url || '#');
        const imageUrl = product.image || product.featured_image?.url || '';
        const image = document.createElement(imageUrl ? 'img' : 'div');
        if (imageUrl) {
          image.src = imageUrl;
          image.alt = product.title || '';
          image.loading = 'lazy';
        } else {
          image.className = 'predictive-placeholder';
          image.setAttribute('aria-hidden', 'true');
        }
        const title = document.createElement('h3');
        title.textContent = product.title || '';
        const collection = document.createElement('p');
        collection.textContent = product.collection || product.product_type || product.vendor || 'Luv Crafts atelier';
        const price = document.createElement('strong');
        price.textContent = formatSearchPrice(product.price);
        link.append(image, title, collection, price);
        card.append(link);
        predictiveResults.append(card);
      });
    };

    const openSearch = () => {
      menu?.classList.remove('is-open');
      menuButton?.setAttribute('aria-expanded', 'false');
      headerSearch.classList.add('is-open');
      searchScrim?.classList.add('is-open');
      document.body.classList.add('is-search-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      headerSearch.setAttribute('aria-hidden', 'false');
      renderSearchProducts(searchCatalog, searchCatalog.length ? 'Suggested pieces' : 'Search the atelier');
      window.setTimeout(() => predictiveInput.focus(), 120);
    };
    const closeSearch = (restoreFocus = true) => {
      headerSearch.classList.remove('is-open');
      searchScrim?.classList.remove('is-open');
      document.body.classList.remove('is-search-open');
      searchToggle.setAttribute('aria-expanded', 'false');
      headerSearch.setAttribute('aria-hidden', 'true');
      if (restoreFocus) searchToggle.focus();
    };
    const runSearch = async () => {
      const query = predictiveInput.value.trim();
      if (searchAll) {
        const allUrl = new URL(searchAll.getAttribute('href'), window.location.href);
        if (query) allUrl.searchParams.set('q', query); else allUrl.searchParams.delete('q');
        searchAll.href = allUrl.toString();
      }
      if (!query) {
        renderSearchProducts(searchCatalog, searchCatalog.length ? 'Suggested pieces' : 'Search the atelier');
        return;
      }
      const predictiveUrl = headerSearch.dataset.predictiveUrl;
      if (!predictiveUrl) {
        const normalized = query.toLowerCase();
        const matches = searchCatalog.filter((product) => `${product.title} ${product.collection}`.toLowerCase().includes(normalized));
        renderSearchProducts(matches, `${matches.length} curated ${matches.length === 1 ? 'result' : 'results'}`);
        return;
      }
      searchRequest?.abort();
      searchRequest = new AbortController();
      try {
        const endpoint = new URL(predictiveUrl.endsWith('.json') ? predictiveUrl : `${predictiveUrl}.json`, window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(endpoint, { signal: searchRequest.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const payload = await response.json();
        const products = payload.resources?.results?.products || [];
        renderSearchProducts(products, `${products.length} ${products.length === 1 ? 'result' : 'results'}`);
      } catch (error) {
        if (error.name !== 'AbortError') renderSearchProducts([], 'Search the atelier');
      }
    };

    searchToggle.addEventListener('click', () => headerSearch.classList.contains('is-open') ? closeSearch() : openSearch());
    searchClose?.addEventListener('click', () => closeSearch());
    searchScrim?.addEventListener('click', () => closeSearch());
    predictiveInput.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(runSearch, 180);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && headerSearch.classList.contains('is-open')) closeSearch();
    });
  }

  document.querySelectorAll('[data-price-range]').forEach((range) => {
    const minSlider = range.querySelector('[data-price-min-slider]');
    const maxSlider = range.querySelector('[data-price-max-slider]');
    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');
    const track = range.querySelector('[data-price-range-track]');
    const ceiling = Number(range.dataset.rangeMax || maxSlider?.max || 0);
    if (!minSlider || !maxSlider || !minInput || !maxInput || !track || !ceiling) return;

    const clamp = (value) => Math.max(0, Math.min(ceiling, Number(value) || 0));
    const updateTrack = (minimum, maximum) => {
      track.style.setProperty('--price-start', `${(minimum / ceiling) * 100}%`);
      track.style.setProperty('--price-end', `${(maximum / ceiling) * 100}%`);
    };
    const syncFromSliders = (source) => {
      let minimum = clamp(minSlider.value);
      let maximum = clamp(maxSlider.value);
      if (minimum > maximum) {
        if (source === minSlider) minimum = maximum;
        else maximum = minimum;
      }
      minSlider.value = String(minimum);
      maxSlider.value = String(maximum);
      minInput.value = String(Math.round(minimum));
      maxInput.value = String(Math.round(maximum));
      updateTrack(minimum, maximum);
    };
    const syncFromInputs = (source) => {
      let minimum = clamp(minInput.value);
      let maximum = clamp(maxInput.value);
      if (minimum > maximum) {
        if (source === minInput) minimum = maximum;
        else maximum = minimum;
      }
      minInput.value = String(Math.round(minimum));
      maxInput.value = String(Math.round(maximum));
      minSlider.value = String(minimum);
      maxSlider.value = String(maximum);
      updateTrack(minimum, maximum);
    };

    [minSlider, maxSlider].forEach((slider) => slider.addEventListener('input', () => syncFromSliders(slider)));
    [minInput, maxInput].forEach((input) => {
      input.addEventListener('input', () => { if (input.value !== '') syncFromInputs(input); });
      input.addEventListener('change', () => syncFromInputs(input));
    });
    range.closest('form')?.addEventListener('reset', () => window.requestAnimationFrame(() => syncFromSliders()));
    syncFromSliders();
  });

  const quickAddIcon = '<svg class="quick-add-bag-icon" viewBox="0 0 36 36" aria-hidden="true"><path class="quick-add-bag-body" d="M6.5 12.5h18l1.5 16H5z"/><path class="quick-add-bag-handle" d="M11 15v-3.2a4.5 4.5 0 0 1 9 0V15"/><path class="quick-add-bag-fold" d="M9 17.5l-1 7M22 17.5l1 7"/><circle class="quick-add-plus-disc" cx="27" cy="9" r="6"/><path class="quick-add-plus-mark" d="M27 6.3v5.4M24.3 9h5.4"/></svg>';
  const initProductCardGalleries = (scope = document) => {
    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!supportsHover || reducedMotion) return;

    scope.querySelectorAll('[data-product-card-gallery]:not([data-gallery-ready])').forEach((gallery) => {
      const sourceFrames = [...gallery.querySelectorAll('.product-card-gallery-frame')];
      const cardLink = gallery.closest('.product-image');
      if (!cardLink || sourceFrames.length < 2) return;

      const seenSources = new Set();
      const frames = sourceFrames.filter((frame) => {
        const source = frame.currentSrc || frame.src || frame.getAttribute('src');
        if (!source || seenSources.has(source)) {
          frame.remove();
          return false;
        }
        seenSources.add(source);
        return true;
      });
      if (frames.length < 2) return;

      const track = document.createElement('div');
      track.className = 'product-card-gallery-track';
      gallery.insertBefore(track, gallery.firstChild);
      frames.forEach((frame) => {
        frame.classList.remove('is-active', 'is-leaving');
        track.appendChild(frame);
      });

      const firstFrameClone = frames[0].cloneNode(true);
      firstFrameClone.classList.add('is-clone');
      firstFrameClone.setAttribute('aria-hidden', 'true');
      track.appendChild(firstFrameClone);

      const progress = gallery.querySelector('.product-card-gallery-progress');
      if (progress) {
        progress.replaceChildren(...frames.map(() => document.createElement('i')));
      }
      const indicators = progress ? [...progress.querySelectorAll('i')] : [];

      gallery.dataset.galleryReady = 'true';
      let activeIndex = 0;
      let startTimer = null;
      let cycleTimer = null;
      let resetTimer = null;

      const updateGalleryState = () => {
        frames.forEach((frame, index) => {
          frame.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
        });
        indicators.forEach((indicator, index) => indicator.classList.toggle('is-active', index === activeIndex));
      };

      const moveToFrame = (nextIndex) => {
        window.clearTimeout(resetTimer);
        const targetIndex = (nextIndex + frames.length) % frames.length;
        const wrapsToFirst = activeIndex === frames.length - 1 && targetIndex === 0;

        activeIndex = targetIndex;
        track.classList.remove('is-resetting');
        track.style.transform = `translate3d(-${wrapsToFirst ? frames.length * 100 : targetIndex * 100}%, 0, 0)`;
        updateGalleryState();

        if (wrapsToFirst) {
          resetTimer = window.setTimeout(() => {
            track.classList.add('is-resetting');
            track.style.transform = 'translate3d(0, 0, 0)';
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => track.classList.remove('is-resetting'));
            });
          }, 660);
        }
      };

      const stopGallery = ({ reset = true } = {}) => {
        window.clearTimeout(startTimer);
        window.clearInterval(cycleTimer);
        window.clearTimeout(resetTimer);
        startTimer = null;
        cycleTimer = null;
        gallery.classList.remove('is-playing');
        if (reset) {
          activeIndex = 0;
          track.classList.add('is-resetting');
          track.style.transform = 'translate3d(0, 0, 0)';
          updateGalleryState();
          window.requestAnimationFrame(() => track.classList.remove('is-resetting'));
        }
      };

      const startGallery = () => {
        if (startTimer !== null || cycleTimer !== null || gallery.classList.contains('is-playing')) return;
        startTimer = window.setTimeout(() => {
          startTimer = null;
          gallery.classList.add('is-playing');
          moveToFrame(activeIndex + 1);
          cycleTimer = window.setInterval(() => moveToFrame(activeIndex + 1), 1450);
        }, 180);
      };

      updateGalleryState();
      cardLink.addEventListener('mouseenter', startGallery);
      cardLink.addEventListener('mousemove', startGallery, { passive: true });
      cardLink.addEventListener('mouseleave', () => stopGallery());
      cardLink.addEventListener('pointerdown', () => stopGallery({ reset: false }));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopGallery();
      });
    });
  };
  initProductCardGalleries();

  const enhancePreviewQuickAdd = (scope = document) => {
    scope.querySelectorAll('.collection-grid .product-card, .related-product-grid .product-card').forEach((card) => {
      if (card.querySelector('.product-card-form, .product-card-options, .product-card-footer')) return;
      const title = card.querySelector('h3')?.textContent.trim() || 'this piece';
      const productUrl = card.querySelector('h3 a, .product-image')?.getAttribute('href') || 'product.html';
      const badge = card.querySelector('.product-image > span');
      const badgeText = badge?.textContent.trim().toLowerCase() || '';
      const colours = card.querySelector('.product-colours');
      const hasColourChoices = Boolean(colours?.querySelector('.product-colour-options') && !colours.classList.contains('product-colours-empty'));
      const needsOptions = hasColourChoices || badgeText.includes('personali');
      const footer = document.createElement('div');
      const choice = document.createElement('div');

      if (badge) {
        let badgeClass = '';
        if (badgeText.includes('personali')) badgeClass = 'product-status-personalised';
        else if (badgeText.includes('limited')) badgeClass = 'product-status-limited-edition';
        else if (badgeText.includes('gift')) badgeClass = 'product-status-gift-ready';
        else if (badgeText.includes('palette')) badgeClass = 'product-status-new-palette';
        if (badgeClass) badge.className = `product-status-badge ${badgeClass}`;
      }

      footer.className = `product-card-footer${hasColourChoices ? ' has-colour-options' : ''}`;
      choice.className = 'product-card-choice';
      if (hasColourChoices) {
        choice.append(colours);
      } else {
        colours?.remove();
        const detailLink = document.createElement('a');
        detailLink.className = 'product-card-detail-link';
        detailLink.href = localizedStoreUrl(productUrl);
        detailLink.innerHTML = 'View details <span aria-hidden="true">↗</span>';
        choice.append(detailLink);
      }
      footer.append(choice);
      card.querySelector('.product-spec')?.remove();

      if (needsOptions) {
        const optionsLink = document.createElement('a');
        optionsLink.className = 'product-card-options';
        optionsLink.href = localizedStoreUrl(productUrl);
        optionsLink.innerHTML = 'Choose options <span aria-hidden="true">→</span>';
        footer.append(optionsLink);
        card.append(footer);
        return;
      }

      const form = document.createElement('form');
      const status = document.createElement('span');
      const button = document.createElement('button');
      form.className = 'product-card-form product-card-form-preview';
      form.dataset.previewQuickAdd = '';
      status.className = 'product-quick-add-status';
      status.dataset.quickAddStatus = '';
      status.setAttribute('aria-live', 'polite');
      button.className = 'product-quick-add';
      button.type = 'button';
      button.setAttribute('aria-label', `Add to bag — ${title}`);
      button.innerHTML = `${quickAddIcon}<span>Add to bag</span>`;
      form.append(status, button);
      footer.append(form);
      card.append(footer);
    });
  };
  enhancePreviewQuickAdd();

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.product-card-form-preview .product-quick-add');
    if (!button) return;
    const form = button.closest('.product-card-form');
    const status = form?.querySelector('[data-quick-add-status]');
    const count = document.querySelector('.bag-count');
    button.classList.add('is-added');
    if (status) status.textContent = 'Added';
    if (count) count.textContent = String((Number(count.textContent) || 0) + 1);
    window.setTimeout(() => {
      button.classList.remove('is-added');
      if (status) status.textContent = '';
    }, 1600);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.product-card-form:not(.product-card-form-preview)');
    const root = window.Shopify?.routes?.root;
    if (!form || !root || form.dataset.submitting === 'true') return;
    event.preventDefault();
    const button = form.querySelector('.product-quick-add');
    const status = form.querySelector('[data-quick-add-status]');
    form.dataset.submitting = 'true';
    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'This piece could not be added.');
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      document.querySelectorAll('.bag-count').forEach((count) => { if (cart) count.textContent = String(cart.item_count); });
      const source = form.closest('[data-product-recommendations]') ? 'product_recommendation_quick_add' : 'product_quick_add';
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { cart, source, itemKey: payload.key, itemTitle: payload.product_title } }));
      pulseBagIcon();
      button?.classList.add('is-added');
      if (status) status.textContent = 'Added';
      window.setTimeout(() => {
        button?.classList.remove('is-added');
        if (status) status.textContent = '';
      }, 1800);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      delete form.dataset.submitting;
      button?.classList.remove('is-loading');
      if (button) button.disabled = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.product-form');
    const root = window.Shopify?.routes?.root;
    if (!form || !root || form.dataset.submitting === 'true') return;
    event.preventDefault();
    if (!form.reportValidity()) return;
    const button = event.submitter?.matches('[data-product-submit]') ? event.submitter : form.querySelector('[data-product-submit]');
    if (!button || button.disabled) return;
    const originalLabel = button.dataset.addLabel || button.textContent.trim();
    form.dataset.submitting = 'true';
    button.disabled = true;
    button.classList.remove('is-added');
    button.classList.add('is-loading');
    button.textContent = button.dataset.loadingLabel || 'Adding your selection…';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || item.message || button.dataset.errorLabel || 'This piece could not be added.');
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      if (cart) document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
      pulseBagIcon();
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = button.dataset.successLabel || 'Added to your bag';
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { cart, source: 'main_product_add', itemKey: item.key, itemTitle: item.product_title } }));
      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.textContent = originalLabel;
        button.disabled = false;
        delete form.dataset.submitting;
      }, 2200);
    } catch (error) {
      button.classList.remove('is-loading');
      button.textContent = originalLabel;
      button.disabled = false;
      delete form.dataset.submitting;
      showCartToast(error.message, { error: true });
    }
  });

  document.querySelectorAll('[data-infinite-loader]').forEach((loader) => {
    const page = loader.closest('.collection-page');
    const grid = page?.querySelector('[data-infinite-grid]');
    const previewTemplate = page?.querySelector('[data-infinite-template]');
    const status = loader.querySelector('[data-infinite-status]');
    let nextLink = loader.querySelector('[data-infinite-next]');
    let loading = false;
    if (!grid || (!nextLink && !previewTemplate)) {
      loader.classList.add('is-complete');
      return;
    }

    const finish = () => {
      loader.classList.remove('is-loading');
      loader.classList.add('is-complete');
      loader.removeAttribute('aria-busy');
      if (status) status.textContent = loader.dataset.completeLabel || 'You have seen the complete collection.';
    };
    const appendPreviewBatch = () => {
      const batchSize = Number(loader.dataset.previewBatchSize || 4);
      const cards = [...previewTemplate.content.querySelectorAll('.product-card')].slice(0, batchSize);
      cards.forEach((card) => grid.append(card));
      enhancePreviewQuickAdd(grid);
      initProductCardGalleries(grid);
      return previewTemplate.content.querySelector('.product-card') !== null;
    };
    const loadNext = async () => {
      if (loading || loader.classList.contains('is-complete')) return;
      loading = true;
      loader.classList.add('is-loading');
      loader.setAttribute('aria-busy', 'true');
      if (status) status.textContent = loader.dataset.loadingLabel || 'Curating the next pieces…';
      try {
        if (previewTemplate) {
          const hasMore = appendPreviewBatch();
          if (!hasMore) finish();
        } else if (nextLink) {
          const response = await fetch(nextLink.href);
          if (!response.ok) throw new Error('Collection page unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextGrid = parsed.querySelector('[data-infinite-grid]');
          nextGrid?.querySelectorAll('.product-card').forEach((card) => grid.append(document.importNode(card, true)));
          enhancePreviewQuickAdd(grid);
          initProductCardGalleries(grid);
          const parsedNext = parsed.querySelector('[data-infinite-next]');
          if (parsedNext) nextLink.href = new URL(parsedNext.getAttribute('href'), response.url).href;
          else { nextLink.remove(); nextLink = null; finish(); }
        }
      } catch (error) {
        if (status) status.textContent = 'More pieces could not be loaded. Please try again.';
      } finally {
        loading = false;
        loader.classList.remove('is-loading');
        loader.removeAttribute('aria-busy');
      }
    };

    if ('IntersectionObserver' in window) {
      const collectionObserver = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) loadNext(); }, { rootMargin: '500px 0px 500px', threshold: .01 });
      collectionObserver.observe(loader);
    } else {
      nextLink?.addEventListener('click', (event) => { event.preventDefault(); loadNext(); });
    }
  });

  document.querySelectorAll('[data-facet-toggle]').forEach((toggle) => {
    const facets = toggle.closest('.collection-page')?.querySelector('.facets');
    if (!facets) return;
    toggle.addEventListener('click', () => {
      const open = facets.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  });

  const reveals = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
    reveals.forEach((element) => observer.observe(element));
  } else {
    reveals.forEach((element) => element.classList.add('is-visible'));
  }

  document.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
    const slides = [...carousel.querySelectorAll('[data-hero-slide]')];
    const chapters = [...carousel.querySelectorAll('[data-hero-dot]')];
    const previous = carousel.querySelector('[data-hero-prev]');
    const next = carousel.querySelector('[data-hero-next]');
    const pauseButton = carousel.querySelector('[data-hero-pause]');
    const copyHeading = carousel.querySelector('[data-hero-copy-heading]');
    const copyEyebrow = carousel.querySelector('[data-hero-copy-eyebrow]');
    const copyText = carousel.querySelector('[data-hero-copy-text]');
    const copyLink = carousel.querySelector('[data-hero-copy-link]');
    const copyDataNode = carousel.querySelector('[data-hero-copy-data]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (slides.length < 2) return;

    let activeIndex = 0;
    let timer;
    let pointerStart = 0;
    let userPaused = reducedMotion;
    let copyData = [];
    try { copyData = JSON.parse(copyDataNode?.textContent || '[]'); } catch (error) { copyData = []; }

    const updateCopy = (index) => {
      const content = copyData[index];
      if (!content) return;
      const copy = carousel.querySelector('[data-hero-copy]');
      copy?.classList.add('is-changing');
      window.setTimeout(() => {
        if (copyEyebrow && content.eyebrow) copyEyebrow.textContent = content.eyebrow;
        if (copyHeading && content.heading) copyHeading.textContent = content.heading;
        if (copyText && content.text) copyText.textContent = content.text;
        if (copyLink && content.buttonLabel && content.buttonLink) {
          copyLink.textContent = content.buttonLabel;
          copyLink.href = localizedStoreUrl(content.buttonLink);
          copyLink.hidden = false;
        } else if (copyLink) copyLink.hidden = true;
        copy?.classList.remove('is-changing');
      }, reducedMotion ? 0 : 150);
    };

    const updatePauseButton = () => {
      if (!pauseButton) return;
      pauseButton.setAttribute('aria-pressed', String(userPaused));
      pauseButton.setAttribute('aria-label', userPaused ? 'Play carousel' : 'Pause carousel');
      pauseButton.querySelector('span').textContent = userPaused ? '▶' : 'Ⅱ';
    };

    const show = (index, userInitiated = false) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      chapters.forEach((chapter, chapterIndex) => {
        const active = chapterIndex === activeIndex;
        chapter.classList.toggle('is-active', active);
        if (active) chapter.setAttribute('aria-current', 'true');
        else chapter.removeAttribute('aria-current');
      });
      updateCopy(activeIndex);
      if (userInitiated) {
        userPaused = true;
        stop();
        updatePauseButton();
      }
    };

    const stop = () => {
      window.clearInterval(timer);
      carousel.classList.add('is-paused');
    };
    const start = () => {
      if (reducedMotion || userPaused || document.hidden) return;
      window.clearInterval(timer);
      carousel.classList.remove('is-paused');
      timer = window.setInterval(() => show(activeIndex + 1), 7000);
    };
    chapters.forEach((chapter, index) => chapter.addEventListener('click', () => show(index, true)));
    previous?.addEventListener('click', () => show(activeIndex - 1, true));
    next?.addEventListener('click', () => show(activeIndex + 1, true));
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', () => { if (!userPaused) start(); });
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget) && !userPaused) start();
    });
    carousel.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; });
    carousel.addEventListener('pointerup', (event) => {
      const distance = event.clientX - pointerStart;
      if (Math.abs(distance) > 55) show(activeIndex + (distance < 0 ? 1 : -1), true);
    });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    pauseButton?.addEventListener('click', () => {
      userPaused = !userPaused;
      if (userPaused) stop(); else start();
      updatePauseButton();
    });
    carousel.classList.add('is-ready');
    updatePauseButton();
    start();
  });

  document.querySelectorAll('[data-cinematic-rail]').forEach((section) => {
    const track = section.querySelector('[data-rail-track]');
    const cards = track ? [...track.querySelectorAll('.product-card')] : [];
    const previous = section.querySelector('[data-rail-prev]');
    const next = section.querySelector('[data-rail-next]');
    const status = section.querySelector('[data-rail-status]');
    const progress = section.querySelector('[data-rail-progress]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!track || !cards.length) return;

    let activeIndex = 0;
    let timer;
    let visible = false;
    let scrollFrame;
    let pointerStart = 0;
    let scrollStart = 0;
    let dragging = false;

    const pad = () => parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    const cardLeft = (card) => card.offsetLeft - track.offsetLeft - pad();
    const updateEdges = () => {
      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const currentScroll = Math.max(0, track.scrollLeft);
      section.classList.toggle('has-more-left', currentScroll > 4);
      section.classList.toggle('has-more-right', currentScroll < maximumScroll - 4);
    };
    const update = (index) => {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      if (status) status.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      if (progress) progress.style.setProperty('--rail-progress', String((activeIndex + 1) / cards.length));
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === cards.length - 1;
      updateEdges();
    };
    const go = (index) => {
      const targetIndex = Math.max(0, Math.min(cards.length - 1, index));
      track.scrollTo({ left: cardLeft(cards[targetIndex]), behavior: reducedMotion ? 'auto' : 'smooth' });
      update(targetIndex);
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (!visible || reducedMotion || document.hidden) return;
      timer = window.setInterval(() => go(activeIndex === cards.length - 1 ? 0 : activeIndex + 1), 6200);
    };
    const restart = () => {
      stop();
      window.setTimeout(start, 220);
    };

    previous?.addEventListener('click', () => { go(activeIndex - 1); restart(); });
    next?.addEventListener('click', () => { go(activeIndex + 1); restart(); });
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const target = track.scrollLeft + pad();
        let nearest = 0;
        let distance = Infinity;
        cards.forEach((card, index) => {
          const difference = Math.abs(card.offsetLeft - track.offsetLeft - target);
          if (difference < distance) { distance = difference; nearest = index; }
        });
        update(nearest);
      });
    }, { passive: true });
    track.addEventListener('pointerdown', (event) => {
      stop();
      if (event.pointerType === 'touch') return;
      pointerStart = event.clientX;
      scrollStart = track.scrollLeft;
      dragging = false;
    });
    track.addEventListener('pointermove', (event) => {
      if (!pointerStart || event.pointerType === 'touch') return;
      const movement = event.clientX - pointerStart;
      if (Math.abs(movement) > 7) dragging = true;
      if (dragging) track.scrollLeft = scrollStart - movement;
    });
    track.addEventListener('pointerup', () => { pointerStart = 0; restart(); });
    track.addEventListener('pointercancel', () => { pointerStart = 0; restart(); });
    track.addEventListener('click', (event) => {
      if (dragging) { event.preventDefault(); dragging = false; }
    }, true);
    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', start);
    section.addEventListener('focusin', stop);
    section.addEventListener('focusout', (event) => { if (!section.contains(event.relatedTarget)) start(); });
    window.addEventListener('resize', updateEdges, { passive: true });
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    if ('IntersectionObserver' in window) {
      const railObserver = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start(); else stop();
      }, { threshold: .32 });
      railObserver.observe(section);
    }
    update(0);
  });

  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const product = select.closest('.product-information');
      const price = product && product.querySelector('[data-product-price]');
      const submit = product && product.querySelector('[data-product-submit]');
      if (price) {
        price.textContent = option.dataset.price || '';
        if (option.dataset.comparePrice) {
          const comparison = document.createElement('s');
          comparison.textContent = option.dataset.comparePrice;
          price.appendChild(comparison);
        }
      }
      if (submit) {
        const available = option.dataset.available === 'true';
        submit.disabled = !available;
        submit.textContent = available ? submit.dataset.addLabel : submit.dataset.soldOutLabel;
      }
      if (option.dataset.mediaId) {
        const gallery = document.querySelector('[data-product-gallery]');
        const mediaThumbnail = gallery && [...gallery.querySelectorAll('[data-media-thumbnail]')].find((thumbnail) => thumbnail.dataset.mediaId === option.dataset.mediaId);
        mediaThumbnail?.click();
      }
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const slides = [...gallery.querySelectorAll('[data-media-slide]')];
    const thumbnails = [...gallery.querySelectorAll('[data-media-thumbnail]')];
    const stage = gallery.querySelector('[data-media-stage]');
    const previous = gallery.querySelector('[data-media-prev]');
    const next = gallery.querySelector('[data-media-next]');
    const count = gallery.querySelector('[data-media-count]');
    const dialog = gallery.querySelector('[data-product-zoom]');
    const zoomImage = dialog?.querySelector('[data-zoom-image]');
    const zoomLevel = dialog?.querySelector('[data-zoom-level]');
    const zoomClose = dialog?.querySelector('[data-zoom-close]');
    const zoomIn = dialog?.querySelector('[data-zoom-in]');
    const zoomOut = dialog?.querySelector('[data-zoom-out]');
    const zoomPrevious = dialog?.querySelector('[data-zoom-prev]');
    const zoomNext = dialog?.querySelector('[data-zoom-next]');
    if (!slides.length) return;

    let activeIndex = 0;
    let zoomScale = 1;
    let pointerStart = 0;
    let suppressZoom = false;
    const imageIndexes = slides.map((slide, index) => slide.dataset.mediaType === 'image' ? index : -1).filter((index) => index >= 0);

    const formatCount = (number) => String(number).padStart(2, '0');
    const show = (index, focusThumbnail = false, scrollThumbnail = true) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === activeIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-selected', String(active));
        thumbnail.tabIndex = active ? 0 : -1;
      });
      if (count) count.textContent = `${formatCount(activeIndex + 1)} / ${formatCount(slides.length)}`;
      if (focusThumbnail && thumbnails[activeIndex]) thumbnails[activeIndex].focus({ preventScroll: true });
      if (scrollThumbnail) thumbnails[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    const updateZoom = () => {
      if (zoomImage) zoomImage.style.setProperty('--zoom-scale', String(zoomScale));
      if (zoomLevel) zoomLevel.textContent = `${Math.round(zoomScale * 100)}%`;
      if (zoomOut) zoomOut.disabled = zoomScale <= 1;
      if (zoomIn) zoomIn.disabled = zoomScale >= 3;
    };
    const loadZoomImage = () => {
      const image = slides[activeIndex]?.querySelector('img');
      if (!image || !zoomImage) return false;
      zoomImage.src = image.currentSrc || image.src;
      zoomImage.alt = image.alt;
      zoomScale = 1;
      updateZoom();
      return true;
    };
    const openZoom = () => {
      if (!dialog || !loadZoomImage()) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };
    const closeZoom = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };
    const moveZoom = (direction) => {
      if (!imageIndexes.length) return;
      const currentImagePosition = Math.max(0, imageIndexes.indexOf(activeIndex));
      const nextImagePosition = (currentImagePosition + direction + imageIndexes.length) % imageIndexes.length;
      show(imageIndexes[nextImagePosition]);
      loadZoomImage();
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => show(index));
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1, true); }
      });
    });
    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));
    gallery.querySelectorAll('[data-media-zoom]').forEach((button) => button.addEventListener('click', (event) => {
      if (suppressZoom) { event.preventDefault(); return; }
      openZoom();
    }));
    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(activeIndex - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(activeIndex + 1); }
    });
    stage?.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; });
    stage?.addEventListener('pointerup', (event) => {
      const distance = event.clientX - pointerStart;
      if (Math.abs(distance) > 55) {
        suppressZoom = true;
        show(activeIndex + (distance < 0 ? 1 : -1));
        window.setTimeout(() => { suppressZoom = false; }, 250);
      }
      pointerStart = 0;
    });
    zoomClose?.addEventListener('click', closeZoom);
    zoomIn?.addEventListener('click', () => { zoomScale = Math.min(3, zoomScale + .5); updateZoom(); });
    zoomOut?.addEventListener('click', () => { zoomScale = Math.max(1, zoomScale - .5); updateZoom(); });
    zoomPrevious?.addEventListener('click', () => moveZoom(-1));
    zoomNext?.addEventListener('click', () => moveZoom(1));
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) closeZoom(); });
    show(0, false, false);
  });

  document.querySelectorAll('[data-personalisation-upload]').forEach((input) => {
    const panel = input.closest('[data-personalisation-panel]');
    const status = panel?.querySelector('[data-upload-status]');
    const preview = panel?.querySelector('[data-upload-preview]');
    const permittedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maximumSize = Number(input.dataset.maxSize) || 10485760;
    const minimumWidth = Number(input.dataset.minWidth) || 1200;
    const minimumHeight = Number(input.dataset.minHeight) || 1200;
    let previewUrl = '';

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.setCustomValidity('');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = '';
      if (preview) { preview.hidden = true; preview.removeAttribute('src'); }
      if (!file) {
        if (status) status.textContent = '';
        return;
      }
      if (!permittedTypes.includes(file.type)) {
        const message = input.dataset.fileTypeError || 'Please choose a JPG, PNG or WEBP image.';
        input.setCustomValidity(message);
        if (status) status.textContent = message;
        return;
      }
      if (file.size > maximumSize) {
        const message = input.dataset.fileSizeError || 'Please choose an image smaller than 10 MB.';
        input.setCustomValidity(message);
        if (status) status.textContent = message;
        return;
      }
      if (preview) {
        previewUrl = URL.createObjectURL(file);
        preview.src = previewUrl;
        preview.hidden = false;
        input.setCustomValidity('Checking image quality…');
        preview.onload = () => {
          if (preview.naturalWidth < minimumWidth || preview.naturalHeight < minimumHeight) {
            const message = input.dataset.fileDimensionError || `Please choose an image at least ${minimumWidth} × ${minimumHeight} pixels.`;
            input.setCustomValidity(message);
            if (status) status.textContent = message;
            return;
          }
          input.setCustomValidity('');
          if (status) status.textContent = `${file.name} · ${(file.size / 1048576).toFixed(1)} MB · ${preview.naturalWidth} × ${preview.naturalHeight} px`;
        };
      }
    });
  });

  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const form = picker.closest('form');
    const select = form?.querySelector('[data-variant-select]');
    const data = form?.querySelector('[data-product-variants]');
    const groups = [...picker.querySelectorAll('[data-option-group]')];
    const submit = form?.querySelector('[data-product-submit]');
    if (!select || !data || !groups.length) return;

    let variants = [];
    try { variants = JSON.parse(data.textContent); } catch (error) { return; }
    const selectedOptions = () => groups.map((group) => group.querySelector('input:checked')?.value || '');
    const matches = (variant, options) => variant.options.every((option, index) => option === options[index]);
    const update = () => {
      const chosen = selectedOptions();
      const variant = variants.find((candidate) => matches(candidate, chosen));

      groups.forEach((group, groupIndex) => {
        const output = group.querySelector('[data-selected-option]');
        const checked = group.querySelector('input:checked');
        if (output && checked) output.textContent = checked.value;
        group.querySelectorAll('input').forEach((input) => {
          const candidateOptions = [...chosen];
          candidateOptions[groupIndex] = input.value;
          input.disabled = !variants.some((candidate) => candidate.available && matches(candidate, candidateOptions));
        });
      });

      if (variant) {
        select.value = String(variant.id);
        select.dispatchEvent(new Event('change', { bubbles: true }));
        if (window.Shopify && window.history?.replaceState) {
          const variantUrl = new URL(window.location.href);
          variantUrl.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', variantUrl);
        }
      } else if (submit) {
        submit.disabled = true;
        submit.textContent = submit.dataset.soldOutLabel;
      }
    };

    groups.forEach((group) => group.addEventListener('change', update));
    update();
  });

  const emitCommerceEvent = (name, detail = {}) => {
    const payload = { event: name, ...detail };
    window.dataLayer?.push(payload);
    document.dispatchEvent(new CustomEvent('luvcrafts:analytics', { detail: payload }));
  };

  const shippingProgressState = window.__luvcraftsShippingProgress || {
    viewed: new Set(),
    completion: new Map(),
  };
  window.__luvcraftsShippingProgress = shippingProgressState;

  const initialiseShippingProgress = (scope = document) => {
    scope.querySelectorAll('[data-shipping-progress]:not([data-progress-initialised])').forEach((progress) => {
      progress.dataset.progressInitialised = 'true';
      const context = progress.dataset.context || 'cart';
      const eligibleSubtotal = Number(progress.dataset.eligibleSubtotal || 0);
      const threshold = Number(progress.dataset.threshold || 0);
      const complete = threshold > 0 && eligibleSubtotal >= threshold;
      const previous = shippingProgressState.completion.get(context);
      const detail = {
        cart_context: context,
        eligible_subtotal: eligibleSubtotal / 100,
        threshold: threshold / 100,
        progress_percent: threshold > 0 ? Math.min(100, Math.round((eligibleSubtotal / threshold) * 100)) : 0,
        currency: progress.dataset.currency,
        shipping_market: progress.dataset.shippingMarket,
        buyer_country: progress.dataset.buyerCountry,
      };

      if (!shippingProgressState.viewed.has(context)) {
        shippingProgressState.viewed.add(context);
        emitCommerceEvent('free_shipping_progress_viewed', detail);
      }
      if (previous === false && complete) emitCommerceEvent('free_shipping_threshold_reached', detail);
      if (previous === true && !complete) emitCommerceEvent('free_shipping_threshold_lost', detail);
      shippingProgressState.completion.set(context, complete);
    });
  };
  initialiseShippingProgress();

  const initialiseCartDrawer = () => {
    let drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || document.documentElement.dataset.cartDrawerInitialised === 'true') return;
    document.documentElement.dataset.cartDrawerInitialised = 'true';
    let returnFocus = null;
    let requestInFlight = false;
    let closeTimer = null;
    const quantityTimers = new WeakMap();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const cartTriggers = () => [...document.querySelectorAll('[data-cart-drawer-open]')];
    const setTriggerState = (expanded) => cartTriggers().forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
    const isOpen = () => drawer?.getAttribute('aria-hidden') === 'false';
    const focusable = () => [...(drawer?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])].filter((element) => !element.hidden && element.offsetParent !== null);
    const restoreAuthoritativeQuantities = (scope = drawer) => {
      scope?.querySelectorAll('[data-cart-drawer-quantity]').forEach((input) => {
        const line = input.closest('[data-cart-line]');
        if (line?.dataset.cartQuantity) input.value = line.dataset.cartQuantity;
      });
    };

    const open = (trigger = null, { animate = true } = {}) => {
      if (!drawer) return;
      window.clearTimeout(closeTimer);
      restoreAuthoritativeQuantities();
      returnFocus = trigger || returnFocus || document.activeElement;
      drawer.classList.remove('is-active', 'is-closing');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      setTriggerState(true);
      if (!animate || reducedMotion) {
        drawer.classList.add('is-active');
        drawer.querySelector('[data-cart-drawer-panel]')?.focus({ preventScroll: true });
      } else {
        drawer.getBoundingClientRect();
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            drawer?.classList.add('is-active');
            window.setTimeout(() => drawer?.querySelector('[data-cart-drawer-panel]')?.focus({ preventScroll: true }), 190);
          });
        });
      }
      emitCommerceEvent('cart_drawer_opened', { item_count: Number(document.querySelector('.bag-count')?.textContent || 0) });
    };

    const close = () => {
      if (!drawer) return;
      window.clearTimeout(closeTimer);
      drawer.classList.remove('is-active');
      drawer.classList.add('is-closing');
      setTriggerState(false);
      closeTimer = window.setTimeout(() => {
        if (!drawer) return;
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-closing');
        document.body.classList.remove('cart-drawer-open');
        if (returnFocus instanceof HTMLElement) returnFocus.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 640);
    };

    const refresh = async ({ reopen = isOpen(), animate = false } = {}) => {
      if (!drawer || requestInFlight) return false;
      requestInFlight = true;
      try {
        const url = new URL(drawer.dataset.sectionUrl || window.Shopify?.routes?.root || '/', window.location.origin);
        url.searchParams.set('section_id', drawer.dataset.sectionId);
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('The shopping bag could not be refreshed.');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = parsed.querySelector('[data-cart-drawer]');
        if (!replacement) throw new Error('The shopping bag response was incomplete.');
        drawer.replaceWith(replacement);
        drawer = replacement;
        initialiseShippingProgress(drawer);
        initialiseGiftWrap(drawer);
        initialiseGiftMessage(drawer);
        initialiseCartUpsell(drawer);
        if (reopen) open(null, { animate });
        return true;
      } catch (error) {
        const status = drawer?.querySelector('[data-cart-drawer-status]');
        if (status) status.textContent = error.message;
        return false;
      } finally {
        requestInFlight = false;
      }
    };

    const changeLine = async (line, quantity, lineElement = null) => {
      if (requestInFlight) return;
      const currentQuantity = Math.max(1, Number(lineElement?.dataset.cartQuantity || 1));
      const input = lineElement?.querySelector('[data-cart-drawer-quantity]');
      const maximum = Number(input?.max || 0);
      const requestedQuantity = maximum > 0 ? Math.min(maximum, quantity) : quantity;
      if (input) input.value = String(Math.max(1, requestedQuantity));
      if (requestedQuantity === currentQuantity) return;
      requestInFlight = true;
      try {
        if (requestedQuantity === 0 && lineElement && !reducedMotion) {
          lineElement.style.setProperty('--cart-line-height', `${lineElement.offsetHeight}px`);
          lineElement.classList.add('is-being-removed');
          await new Promise((resolve) => window.setTimeout(resolve, 680));
        }
        drawer.classList.add('is-updating');
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineElement?.dataset.cartKey || line, quantity: requestedQuantity }),
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'This bag change could not be saved.');
        document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
        if (requestedQuantity === 0) pulseBagIcon('remove');
        emitCommerceEvent(requestedQuantity === 0 ? 'cart_item_removed' : 'cart_quantity_changed', { item_count: cart.item_count, line, quantity: requestedQuantity, currency: cart.currency });
        requestInFlight = false;
        await refresh({ reopen: true, animate: false });
        const cartPage = document.querySelector('.cart-page');
        if (cartPage?.dataset.sectionId) await refreshMainCart(cartPage.dataset.sectionId);
      } catch (error) {
        lineElement?.classList.remove('is-being-removed');
        lineElement?.style.removeProperty('--cart-line-height');
        if (input) input.value = String(currentQuantity);
        const status = drawer?.querySelector('[data-cart-drawer-status]');
        if (status) status.textContent = error.message;
      } finally {
        requestInFlight = false;
        drawer?.classList.remove('is-updating');
      }
    };

    const celebrateAddition = (detail = {}) => {
      if (!drawer) return;
      const addedLine = [...drawer.querySelectorAll('[data-cart-key]')].find((line) => line.dataset.cartKey === detail.itemKey);
      if (!addedLine) return;
      addedLine.classList.remove('is-just-added');
      addedLine.getBoundingClientRect();
      addedLine.classList.add('is-just-added');
      addedLine.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
      window.setTimeout(() => addedLine?.classList.remove('is-just-added'), 2200);
    };

    document.addEventListener('click', async (event) => {
      const disclosureSummary = event.target.closest('.cart-drawer-disclosure > summary');
      if (disclosureSummary) {
        const disclosure = disclosureSummary.closest('.cart-drawer-disclosure');
        window.setTimeout(() => {
          if (!disclosure?.open) return;
          disclosure.parentElement?.querySelectorAll('.cart-drawer-disclosure[open]').forEach((other) => {
            if (other !== disclosure) other.removeAttribute('open');
          });
          const messageInput = disclosure.querySelector('[data-gift-message-input]');
          if (!messageInput) return;
          disclosure.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
          window.setTimeout(() => messageInput.focus({ preventScroll: true }), reducedMotion ? 0 : 260);
        }, 0);
        return;
      }
      const trigger = event.target.closest('[data-cart-drawer-open]');
      if (trigger) {
        event.preventDefault();
        returnFocus = trigger;
        await refresh({ reopen: false });
        open(trigger);
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        close();
        return;
      }
      const quantityStep = event.target.closest('[data-cart-drawer-quantity-step]');
      if (quantityStep) {
        const lineElement = quantityStep.closest('[data-cart-line]');
        const input = lineElement?.querySelector('[data-cart-drawer-quantity]');
        const line = Number(lineElement?.dataset.cartLine || 0);
        if (!input || !line) return;
        window.clearTimeout(quantityTimers.get(input));
        const maximum = Number(input.max || 0);
        const next = Math.max(1, Number(input.value || lineElement.dataset.cartQuantity || 1) + Number(quantityStep.dataset.cartDrawerQuantityStep || 0));
        const quantity = maximum > 0 ? Math.min(maximum, next) : next;
        input.value = String(quantity);
        await changeLine(line, quantity, lineElement);
        return;
      }
      const remove = event.target.closest('[data-cart-drawer-remove]');
      if (remove) {
        const lineElement = remove.closest('[data-cart-line]');
        const line = Number(lineElement?.dataset.cartLine || 0);
        if (line) await changeLine(line, 0, lineElement);
      }
    });

    document.addEventListener('input', (event) => {
      const quantity = event.target.closest('[data-cart-drawer-quantity]');
      if (!quantity) return;
      window.clearTimeout(quantityTimers.get(quantity));
      const lineElement = quantity.closest('[data-cart-line]');
      const line = Number(lineElement?.dataset.cartLine || 0);
      if (!line) return;
      const maximum = Number(quantity.max || 0);
      const requested = Math.max(1, Number(quantity.value || 1));
      const next = maximum > 0 ? Math.min(maximum, requested) : requested;
      const timer = window.setTimeout(() => changeLine(line, next, lineElement), 550);
      quantityTimers.set(quantity, timer);
    });

    document.addEventListener('change', async (event) => {
      const quantity = event.target.closest('[data-cart-drawer-quantity]');
      if (!quantity) return;
      window.clearTimeout(quantityTimers.get(quantity));
      const line = Number(quantity.closest('[data-cart-line]')?.dataset.cartLine || 0);
      if (line) await changeLine(line, Math.max(1, Number(quantity.value || 1)), quantity.closest('[data-cart-line]'));
    });

    document.addEventListener('keydown', (event) => {
      if (!isOpen()) return;
      const quantity = event.target.closest('[data-cart-drawer-quantity]');
      if (quantity && event.key === 'Enter') {
        event.preventDefault();
        window.clearTimeout(quantityTimers.get(quantity));
        const lineElement = quantity.closest('[data-cart-line]');
        const line = Number(lineElement?.dataset.cartLine || 0);
        if (line) changeLine(line, Math.max(1, Number(quantity.value || 1)), lineElement);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('luvcrafts:cart-updated', async (event) => {
      const source = event.detail?.source;
      const shouldOpen = ['main_product_add', 'sticky_add_to_cart', 'product_quick_add', 'product_recommendation_quick_add'].includes(source);
      const shouldCelebrate = shouldOpen || source === 'cart_upsell_drawer';
      await refresh({ reopen: shouldOpen || isOpen(), animate: shouldOpen });
      if (shouldCelebrate) celebrateAddition(event.detail);
    });
  };
  initialiseCartDrawer();

  const giftWrapState = window.__luvcraftsGiftWrap || { viewed: new Set(), unavailable: new Set(), syncing: false };
  window.__luvcraftsGiftWrap = giftWrapState;

  const celebrateMainCartAddition = (cartPage, itemKey, variantId) => {
    if (!cartPage || (!itemKey && !variantId)) return;
    const addedLine = [...cartPage.querySelectorAll('.cart-line[data-cart-key]')]
      .find((line) => line.dataset.cartKey === itemKey || line.dataset.cartVariantId === String(variantId));
    if (!addedLine) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    addedLine.classList.remove('is-just-added');
    addedLine.getBoundingClientRect();
    addedLine.classList.add('is-just-added');
    addedLine.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
    window.setTimeout(() => addedLine?.classList.remove('is-just-added'), 2200);
  };

  const refreshMainCart = async (sectionId, { addedItemKey = '', addedVariantId = '' } = {}) => {
    const current = document.querySelector('.cart-page');
    if (!sectionId || !current) return;
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}cart`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) return;
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const replacement = parsed.querySelector('.cart-page');
    if (!replacement) return;
    current.replaceWith(replacement);
    initialiseShippingProgress(replacement);
    initialiseGiftWrap(replacement);
    initialiseGiftMessage(replacement);
    initialiseCartUpsell(replacement);
    if (addedItemKey || addedVariantId) celebrateMainCartAddition(replacement, addedItemKey, addedVariantId);
    return replacement;
  };

  let mainCartRequestInFlight = false;
  const mainCartQuantityTimers = new WeakMap();

  const changeMainCartLine = async (lineElement, quantity) => {
    if (mainCartRequestInFlight || !lineElement) return;
    const cartPage = lineElement.closest('.cart-page');
    const status = cartPage?.querySelector('[data-cart-page-status]');
    const input = lineElement.querySelector('[data-cart-page-quantity]');
    const currentQuantity = Math.max(1, Number(lineElement.dataset.cartQuantity || 1));
    const maximum = Number(input?.max || 0);
    const requestedQuantity = quantity === 0 ? 0 : Math.max(1, maximum > 0 ? Math.min(maximum, quantity) : quantity);
    if (!cartPage?.dataset.sectionId || !lineElement.dataset.cartKey) return;
    if (input) input.value = String(requestedQuantity || currentQuantity);
    if (requestedQuantity === currentQuantity) return;

    mainCartRequestInFlight = true;
    cartPage.classList.add('is-updating');
    try {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (requestedQuantity === 0 && !reducedMotion) {
        lineElement.style.setProperty('--cart-line-height', `${lineElement.offsetHeight}px`);
        lineElement.classList.add('is-being-removed');
        await new Promise((resolve) => window.setTimeout(resolve, 680));
      }
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineElement.dataset.cartKey, quantity: requestedQuantity }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'This bag change could not be saved.');
      document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
      if (requestedQuantity === 0) pulseBagIcon('remove');
      emitCommerceEvent(requestedQuantity === 0 ? 'cart_item_removed' : 'cart_quantity_changed', {
        item_count: cart.item_count,
        quantity: requestedQuantity,
        currency: cart.currency,
      });
      await refreshMainCart(cartPage.dataset.sectionId);
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', {
        detail: { cart, source: requestedQuantity === 0 ? 'main_cart_remove' : 'main_cart_quantity' },
      }));
    } catch (error) {
      lineElement.classList.remove('is-being-removed');
      lineElement.style.removeProperty('--cart-line-height');
      if (input) input.value = String(currentQuantity);
      if (status) status.textContent = error.message;
    } finally {
      mainCartRequestInFlight = false;
      document.querySelector('.cart-page')?.classList.remove('is-updating');
    }
  };

  document.addEventListener('click', async (event) => {
    const disclosureSummary = event.target.closest('.cart-page .cart-page-disclosure > summary');
    if (disclosureSummary) {
      const disclosure = disclosureSummary.closest('.cart-page-disclosure');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => {
        if (!disclosure?.open) return;
        const disclosureGroup = disclosure.parentElement;
        disclosureGroup?.querySelectorAll(':scope > .cart-page-disclosure[open]').forEach((other) => {
          if (other !== disclosure) other.removeAttribute('open');
        });
        const messageInput = disclosure.querySelector('[data-gift-message-input]');
        const noteInput = disclosure.querySelector('#CartNote');
        const focusTarget = messageInput || noteInput;
        if (!focusTarget) return;
        disclosure.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
        window.setTimeout(() => focusTarget.focus({ preventScroll: true }), reducedMotion ? 0 : 220);
      }, 0);
      return;
    }

    const quantityStep = event.target.closest('.cart-page [data-cart-page-quantity-step]');
    if (quantityStep) {
      event.preventDefault();
      const lineElement = quantityStep.closest('.cart-line[data-cart-key]');
      const input = lineElement?.querySelector('[data-cart-page-quantity]');
      if (!lineElement || !input) return;
      window.clearTimeout(mainCartQuantityTimers.get(input));
      const maximum = Number(input.max || 0);
      const next = Math.max(1, Number(input.value || lineElement.dataset.cartQuantity || 1) + Number(quantityStep.dataset.cartPageQuantityStep || 0));
      await changeMainCartLine(lineElement, maximum > 0 ? Math.min(maximum, next) : next);
      return;
    }

    const remove = event.target.closest('.cart-page .cart-line-remove');
    if (!remove) return;
    event.preventDefault();
    const lineElement = remove.closest('.cart-line[data-cart-line]');
    await changeMainCartLine(lineElement, 0);
  });

  document.addEventListener('input', (event) => {
    const quantity = event.target.closest('.cart-page [data-cart-page-quantity]');
    if (!quantity) return;
    window.clearTimeout(mainCartQuantityTimers.get(quantity));
    const lineElement = quantity.closest('.cart-line[data-cart-key]');
    if (!lineElement) return;
    const maximum = Number(quantity.max || 0);
    const requested = Math.max(1, Number(quantity.value || 1));
    const next = maximum > 0 ? Math.min(maximum, requested) : requested;
    const timer = window.setTimeout(() => changeMainCartLine(lineElement, next), 550);
    mainCartQuantityTimers.set(quantity, timer);
  });

  document.addEventListener('change', async (event) => {
    const quantity = event.target.closest('.cart-page [data-cart-page-quantity]');
    if (!quantity) return;
    window.clearTimeout(mainCartQuantityTimers.get(quantity));
    await changeMainCartLine(quantity.closest('.cart-line[data-cart-key]'), Math.max(1, Number(quantity.value || 1)));
  });

  document.addEventListener('keydown', (event) => {
    const quantity = event.target.closest('.cart-page [data-cart-page-quantity]');
    if (!quantity || event.key !== 'Enter') return;
    event.preventDefault();
    window.clearTimeout(mainCartQuantityTimers.get(quantity));
    changeMainCartLine(quantity.closest('.cart-line[data-cart-key]'), Math.max(1, Number(quantity.value || 1)));
  });

  let uniqueCartNormalising = false;
  const normaliseUniqueCartLines = async () => {
    if (uniqueCartNormalising) return;
    const uniqueLines = [...document.querySelectorAll('[data-cart-unique="true"][data-cart-key][data-cart-quantity]')];
    const updates = {};
    uniqueLines.forEach((line) => {
      if (Number(line.dataset.cartQuantity || 1) > 1) {
        updates[line.dataset.cartKey] = 1;
        line.dataset.cartQuantity = '1';
        line.classList.add('is-normalising');
      }
    });
    if (!Object.keys(updates).length) return;

    uniqueCartNormalising = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'The unique piece quantity could not be corrected.');
      document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
      emitCommerceEvent('cart_quantity_changed', { item_count: cart.item_count, quantity: 1, currency: cart.currency, unique_piece: true });

      const cartPage = document.querySelector('.cart-page');
      if (cartPage?.dataset.sectionId) await refreshMainCart(cartPage.dataset.sectionId);
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { source: 'unique_quantity_normalisation' } }));
      const status = document.querySelector('[data-unique-cart-status]');
      if (status) status.textContent = 'Unique pieces are reserved as one item each.';
    } catch (error) {
      const status = document.querySelector('[data-unique-cart-status], [data-cart-drawer-status]');
      if (status) status.textContent = error.message;
    } finally {
      uniqueCartNormalising = false;
      document.querySelectorAll('.is-normalising').forEach((line) => line.classList.remove('is-normalising'));
    }
  };

  const uniqueCartObserver = new MutationObserver(() => { normaliseUniqueCartLines(); });
  uniqueCartObserver.observe(document.body, { childList: true, subtree: true });
  normaliseUniqueCartLines();

  const updateGiftWrap = async (option, selected, { automatic = false } = {}) => {
    if (!option || option.dataset.updating === 'true' || giftWrapState.syncing) return;
    const toggle = option.querySelector('[data-gift-wrap-toggle]');
    const status = option.querySelector('[data-gift-wrap-status]');
    const variantId = Number(option.dataset.variantId || 0);
    const currentQuantity = Number(option.dataset.currentQuantity || 0);
    const targetQuantity = Number(option.dataset.targetQuantity || 0);
    const lineKeys = (option.dataset.lineKeys || '').split('|').filter(Boolean);
    const root = window.Shopify?.routes?.root || '/';
    option.dataset.updating = 'true';
    giftWrapState.syncing = true;
    option.classList.add('is-updating');
    if (toggle) toggle.disabled = true;
    if (status) status.textContent = selected ? 'Adding the atelier finish…' : 'Removing gift wrap…';

    try {
      let response;
      if (selected && currentQuantity === 0) {
        if (!variantId || targetQuantity < 1) throw new Error('Gift wrapping is not currently available.');
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: targetQuantity, properties: { _luvcrafts_gift_wrap: 'true' } }] }),
        });
      } else {
        const updates = {};
        lineKeys.forEach((key, index) => { updates[key] = selected && index === 0 ? targetQuantity : 0; });
        response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Gift wrapping could not be updated.');
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : result;
      document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
      emitCommerceEvent(selected ? 'gift_wrap_added' : 'gift_wrap_removed', {
        gift_wrap_variant_id: variantId,
        quantity: selected ? targetQuantity : 0,
        value: Number(option.dataset.priceMinor || 0) / 100,
        cart_value: Number(option.dataset.cartValue || 0) / 100,
        currency: option.dataset.currency,
        cart_context: option.dataset.context,
        automatic,
      });
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { cart, source: 'gift_wrap' } }));
      const cartPage = document.querySelector('.cart-page');
      if (cartPage?.dataset.sectionId) await refreshMainCart(cartPage.dataset.sectionId);
    } catch (error) {
      if (toggle) toggle.checked = !selected;
      if (status) status.textContent = error.message;
      emitCommerceEvent('gift_wrap_error', { cart_context: option.dataset.context, error_type: 'cart_update' });
    } finally {
      giftWrapState.syncing = false;
      delete option.dataset.updating;
      option.classList.remove('is-updating');
      if (toggle) toggle.disabled = false;
    }
  };

  function initialiseGiftWrap(scope = document) {
    scope.querySelectorAll('[data-gift-wrap-option]:not([data-gift-wrap-initialised])').forEach((option) => {
      option.dataset.giftWrapInitialised = 'true';
      const context = option.dataset.context || 'cart';
      const toggle = option.querySelector('[data-gift-wrap-toggle]');
      const key = `${context}:${option.dataset.variantId || 'unconfigured'}`;
      if (!giftWrapState.viewed.has(key)) {
        giftWrapState.viewed.add(key);
        emitCommerceEvent('gift_wrap_viewed', { gift_wrap_variant_id: option.dataset.variantId, cart_context: context, currency: option.dataset.currency, value: Number(option.dataset.priceMinor || 0) / 100, cart_value: Number(option.dataset.cartValue || 0) / 100 });
      }
      if (toggle?.disabled && !giftWrapState.unavailable.has(key)) {
        giftWrapState.unavailable.add(key);
        emitCommerceEvent('gift_wrap_unavailable', { gift_wrap_variant_id: option.dataset.variantId, cart_context: context });
      }
      const current = Number(option.dataset.currentQuantity || 0);
      const target = Number(option.dataset.targetQuantity || 0);
      if (toggle?.checked && current !== target) {
        window.setTimeout(() => updateGiftWrap(option, target > 0, { automatic: true }), 0);
      }
    });
  }
  initialiseGiftWrap();

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('[data-gift-wrap-toggle]');
    if (!toggle) return;
    const option = toggle.closest('[data-gift-wrap-option]');
    emitCommerceEvent('gift_wrap_selected', { selected: toggle.checked, cart_context: option?.dataset.context, gift_wrap_variant_id: option?.dataset.variantId });
    updateGiftWrap(option, toggle.checked);
  });

  const giftMessageState = window.__luvcraftsGiftMessage || {
    initialised: false,
    message: '',
    isGift: false,
    dirty: false,
    saving: null,
    timer: null,
    started: false,
  };
  window.__luvcraftsGiftMessage = giftMessageState;

  const giftMessageOptions = () => [...document.querySelectorAll('[data-gift-message]')];
  const setGiftMessageStatus = (text) => giftMessageOptions().forEach((option) => {
    const status = option.querySelector('[data-gift-message-status]');
    if (status) status.textContent = text;
  });

  const syncGiftMessageControls = (source = null) => {
    giftMessageOptions().forEach((option) => {
      const input = option.querySelector('[data-gift-message-input]');
      const toggle = option.querySelector('[data-gift-message-toggle]');
      const fields = option.querySelector('[data-gift-message-fields]');
      const count = option.querySelector('[data-gift-message-count]');
      const mode = option.dataset.displayMode;
      const wrapSelected = option.dataset.wrapSelected === 'true';
      const visible = mode === 'always'
        || (mode === 'toggle' && (giftMessageState.isGift || giftMessageState.message.length > 0))
        || (mode === 'gift_wrap' && (wrapSelected || giftMessageState.message.length > 0));
      option.classList.toggle('is-open', visible);
      if (fields) fields.hidden = !visible;
      if (toggle && toggle !== source) toggle.checked = giftMessageState.isGift;
      if (input && input !== source) input.value = giftMessageState.message;
      if (count) count.textContent = String(giftMessageState.message.length);
    });
  };

  const saveGiftMessage = async ({ force = false } = {}) => {
    window.clearTimeout(giftMessageState.timer);
    giftMessageState.timer = null;
    if (giftMessageState.saving) return giftMessageState.saving;
    if (!giftMessageState.dirty && !force) return true;
    const message = giftMessageState.message.trim();
    giftMessageState.message = message;
    syncGiftMessageControls();
    const option = giftMessageOptions()[0];
    const savingLabel = option?.dataset.savingLabel || 'Saving your message…';
    const savedLabel = option?.dataset.savedLabel || 'Message saved';
    const errorLabel = option?.dataset.errorLabel || 'Your message could not be saved. Please retry.';
    setGiftMessageStatus(savingLabel);

    giftMessageState.saving = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Gift message': message, 'Is gift': giftMessageState.isGift ? 'Yes' : '' } }),
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || errorLabel);
        giftMessageState.dirty = false;
        giftMessageOptions().forEach((surface) => {
          surface.dataset.savedMessage = message;
          surface.dataset.savedIsGift = String(giftMessageState.isGift);
        });
        setGiftMessageStatus(savedLabel);
        emitCommerceEvent(message ? 'gift_message_saved' : 'gift_message_removed', {
          has_message: Boolean(message),
          character_count: message.length,
          is_gift: giftMessageState.isGift,
        });
        return true;
      } catch (error) {
        giftMessageState.dirty = true;
        setGiftMessageStatus(error.message || errorLabel);
        emitCommerceEvent('gift_message_error', {
          has_message: Boolean(message),
          character_count: message.length,
          is_gift: giftMessageState.isGift,
          error_type: 'cart_attribute_update',
        });
        return false;
      } finally {
        giftMessageState.saving = null;
      }
    })();
    return giftMessageState.saving;
  };

  function initialiseGiftMessage(scope = document) {
    scope.querySelectorAll('[data-gift-message]:not([data-gift-message-initialised])').forEach((option) => {
      option.dataset.giftMessageInitialised = 'true';
      if (!giftMessageState.initialised) {
        giftMessageState.message = option.dataset.savedMessage || '';
        giftMessageState.isGift = option.dataset.savedIsGift === 'true';
        giftMessageState.initialised = true;
      } else if (!giftMessageState.dirty && !giftMessageState.saving) {
        giftMessageState.message = option.dataset.savedMessage || '';
        giftMessageState.isGift = option.dataset.savedIsGift === 'true';
      }
      syncGiftMessageControls();
    });
  }
  initialiseGiftMessage();

  const cartUpsellViewed = window.__luvcraftsCartUpsellViewed || new Set();
  window.__luvcraftsCartUpsellViewed = cartUpsellViewed;
  const initialiseCartUpsell = (scope = document) => {
    scope.querySelectorAll('[data-cart-upsell]:not([data-cart-upsell-initialised])').forEach((upsell) => {
      upsell.dataset.cartUpsellInitialised = 'true';
      const context = upsell.dataset.context || 'cart';
      if (cartUpsellViewed.has(context)) return;
      const offers = [...upsell.querySelectorAll('[data-upsell-product-id]')];
      if (!offers.length) return;
      cartUpsellViewed.add(context);
      emitCommerceEvent('cart_upsell_viewed', {
        cart_context: context,
        offer_count: offers.length,
        upsell_product_ids: offers.map((offer) => offer.dataset.upsellProductId),
      });
    });
  };
  initialiseCartUpsell();

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button) return;
    const control = button.closest('[data-cart-upsell-form]');
    const offer = button.closest('[data-upsell-product-id]');
    if (!control || !offer || control.dataset.submitting === 'true') return;
    const status = control.querySelector('[data-quick-add-status]');
    control.dataset.submitting = 'true';
    button.disabled = true;
    button.classList.add('is-loading');
    if (status) status.textContent = 'Adding…';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(control.dataset.variantId), quantity: 1 }] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This companion could not be added.');
      const addedItem = result.items?.[0] || result;
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      if (cart) document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
      emitCommerceEvent('cart_upsell_added', {
        upsell_product_id: offer.dataset.upsellProductId,
        upsell_variant_id: offer.dataset.upsellVariantId,
        upsell_value: Number(offer.dataset.priceMinor || 0) / 100,
        currency: offer.dataset.currency,
      });
      const upsellSection = offer.closest('[data-cart-upsell]');
      const upsellContext = upsellSection?.dataset.context || 'page';
      const source = upsellContext === 'drawer' ? 'cart_upsell_drawer' : 'cart_upsell_page';
      document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { cart, source, itemKey: addedItem.key, itemTitle: addedItem.product_title } }));
      pulseBagIcon();
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      if (status) status.textContent = 'Added';
      const cartPage = document.querySelector('.cart-page');
      if (cartPage?.dataset.sectionId) {
        await refreshMainCart(cartPage.dataset.sectionId, {
          addedItemKey: addedItem.key,
          addedVariantId: addedItem.variant_id || addedItem.id,
        });
      }
    } catch (error) {
      if (status) status.textContent = error.message;
      button.disabled = false;
      button.classList.remove('is-loading');
      delete control.dataset.submitting;
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-gift-message-input]');
    if (!input) return;
    giftMessageState.message = input.value;
    if (input.closest('[data-gift-message]')?.dataset.autoGift === 'true') giftMessageState.isGift = giftMessageState.message.trim().length > 0;
    giftMessageState.dirty = true;
    if (!giftMessageState.started && giftMessageState.message.length > 0) {
      giftMessageState.started = true;
      emitCommerceEvent('gift_message_started', { has_message: true, character_count: giftMessageState.message.length, is_gift: giftMessageState.isGift });
    }
    syncGiftMessageControls(input);
    window.clearTimeout(giftMessageState.timer);
    giftMessageState.timer = window.setTimeout(() => saveGiftMessage(), 800);
  });

  document.addEventListener('blur', (event) => {
    if (event.target.matches('[data-gift-message-input]')) saveGiftMessage();
  }, true);

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('[data-gift-message-toggle]');
    if (!toggle) return;
    giftMessageState.isGift = toggle.checked;
    if (!toggle.checked && toggle.closest('[data-gift-message]')?.dataset.displayMode === 'toggle') giftMessageState.message = '';
    giftMessageState.dirty = true;
    syncGiftMessageControls(toggle);
    saveGiftMessage();
  });

  document.addEventListener('submit', async (event) => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    if (document.documentElement.dataset.checkoutInProgress === 'true') return;
    document.documentElement.dataset.checkoutInProgress = 'true';
    event.submitter.disabled = true;
    const saved = await saveGiftMessage({ force: giftMessageState.dirty });
    if (saved) {
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}checkout`);
      return;
    }
    delete document.documentElement.dataset.checkoutInProgress;
    event.submitter.disabled = false;
  }, true);

  const initialiseStickyPurchase = (scope = document) => {
    scope.querySelectorAll('[data-sticky-purchase]:not([data-sticky-initialised])').forEach((sticky) => {
      sticky.dataset.stickyInitialised = 'true';
      const form = document.getElementById(sticky.dataset.productFormId);
      const purchaseActions = form?.querySelector('[data-primary-purchase-actions]');
      const mainSubmit = form?.querySelector('[data-product-submit]');
      const variantSelect = form?.querySelector('[data-variant-select]');
      const mainQuantity = form?.querySelector('[name="quantity"]');
      const stickyQuantity = sticky.querySelector('[data-sticky-quantity]');
      const stickySubmit = sticky.querySelector('[data-sticky-submit]');
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      const stickyCompare = sticky.querySelector('[data-sticky-compare]');
      const stickyVariant = sticky.querySelector('[data-sticky-variant]');
      const stickyAvailability = sticky.querySelector('[data-sticky-availability]');
      const stickyMedia = sticky.querySelector('[data-sticky-media]');
      const stickyImage = stickyMedia?.querySelector('img');
      const status = sticky.querySelector('[data-sticky-status]');
      if (!form || !purchaseActions || !mainSubmit || !stickySubmit) return;

      let purchaseControlsPassed = false;
      let viewed = false;
      const option = () => variantSelect?.options[variantSelect.selectedIndex] || null;
      const analyticsDetail = (quantity = Number(mainQuantity?.value || 1)) => ({
        product_id: sticky.dataset.productId,
        variant_id: option()?.value || sticky.dataset.variantId,
        product_title: sticky.dataset.productTitle,
        variant_title: option()?.dataset.variantTitle || sticky.dataset.variantTitle,
        quantity,
        value: Number(option()?.dataset.priceMinor || sticky.dataset.priceMinor || 0) / 100,
        currency: sticky.dataset.currency,
        page_location: window.location.href,
      });
      const isAvailable = () => (option()?.dataset.available || sticky.dataset.available) === 'true' && !mainSubmit.disabled;
      const setVisibility = () => {
        const visible = purchaseControlsPassed && isAvailable();
        sticky.setAttribute('aria-hidden', String(!visible));
        if (visible && !viewed) {
          viewed = true;
          emitCommerceEvent('sticky_add_to_cart_viewed', analyticsDetail());
        }
      };
      const sync = () => {
        const selected = option();
        const available = isAvailable();
        if (selected) {
          if (stickyPrice) stickyPrice.textContent = selected.dataset.price || '';
          if (stickyCompare) {
            stickyCompare.textContent = selected.dataset.comparePrice || '';
            stickyCompare.hidden = !selected.dataset.comparePrice;
          }
          if (stickyVariant) {
            stickyVariant.textContent = selected.dataset.variantTitle || selected.textContent.split('—')[0].trim();
            stickyVariant.hidden = !selected.dataset.variantTitle;
          }
          if (stickyImage && selected.dataset.mediaSrc) {
            stickyImage.src = selected.dataset.mediaSrc;
            stickyMedia.hidden = false;
          }
          sticky.dataset.variantId = selected.value;
          sticky.dataset.variantTitle = selected.dataset.variantTitle || '';
          sticky.dataset.priceMinor = selected.dataset.priceMinor || '0';
          sticky.dataset.available = selected.dataset.available || 'false';
          [mainQuantity, stickyQuantity].filter(Boolean).forEach((quantityInput) => {
            const minimum = Number(selected.dataset.quantityMin || 1);
            const maximum = Number(selected.dataset.quantityMax || 0);
            quantityInput.min = String(minimum);
            quantityInput.step = selected.dataset.quantityIncrement || '1';
            if (maximum) quantityInput.max = String(maximum);
            else quantityInput.removeAttribute('max');
            const current = Number(quantityInput.value || minimum);
            quantityInput.value = String(Math.max(minimum, maximum ? Math.min(maximum, current) : current));
          });
        }
        if (stickyAvailability) stickyAvailability.textContent = available ? stickyAvailability.dataset.availableLabel || 'Available' : stickySubmit.dataset.soldOutLabel;
        stickySubmit.disabled = !available;
        stickySubmit.textContent = available ? stickySubmit.dataset.addLabel : stickySubmit.dataset.soldOutLabel;
        if (stickyQuantity && mainQuantity) stickyQuantity.value = mainQuantity.value;
        setVisibility();
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          purchaseControlsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          setVisibility();
        }, { threshold: 0 });
        observer.observe(purchaseActions);
      }

      variantSelect?.addEventListener('change', sync);
      form.addEventListener('change', (event) => {
        if (event.target.matches('[name="selling_plan"], [name="quantity"]')) sync();
      });
      mainQuantity?.addEventListener('input', () => {
        if (stickyQuantity) stickyQuantity.value = mainQuantity.value;
      });
      stickyQuantity?.addEventListener('input', () => {
        if (!mainQuantity) return;
        mainQuantity.value = stickyQuantity.value;
        mainQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      });
      stickyQuantity?.addEventListener('change', () => {
        if (!mainQuantity) return;
        mainQuantity.value = stickyQuantity.value;
        mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      });

      stickySubmit.addEventListener('click', async () => {
        if (sticky.dataset.submitting === 'true' || !isAvailable()) return;
        if (!form.reportValidity()) {
          const invalid = form.querySelector(':invalid');
          invalid?.focus({ preventScroll: false });
          return;
        }
        const quantity = Number(mainQuantity?.value || 1);
        sticky.dataset.submitting = 'true';
        stickySubmit.disabled = true;
        stickySubmit.classList.add('is-loading');
        if (status) status.textContent = stickySubmit.dataset.loadingLabel || 'Adding your selection…';
        emitCommerceEvent('sticky_add_to_cart_clicked', analyticsDetail(quantity));

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || stickySubmit.dataset.errorLabel || 'This piece could not be added.');
          const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = cartResponse.ok ? await cartResponse.json() : null;
          if (cart) document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = String(cart.item_count); });
          emitCommerceEvent('sticky_add_to_cart_success', analyticsDetail(quantity));
          pulseBagIcon();
          document.dispatchEvent(new CustomEvent('luvcrafts:cart-updated', { detail: { cart, source: 'sticky_add_to_cart', itemKey: result.key, itemTitle: result.product_title } }));
          if (status) status.textContent = stickySubmit.dataset.successLabel || 'Added to your bag.';
          if (!document.querySelector('[data-cart-drawer]')) window.location.assign(`${root}cart`);
        } catch (error) {
          emitCommerceEvent('sticky_add_to_cart_error', { ...analyticsDetail(quantity), error_type: 'cart_add' });
          if (status) status.textContent = error.message;
          stickySubmit.disabled = false;
          stickySubmit.classList.remove('is-loading');
          delete sticky.dataset.submitting;
        }
      });
      sync();
    });
  };
  initialiseStickyPurchase();
  document.addEventListener('shopify:section:load', (event) => initialiseStickyPurchase(event.target));

  document.querySelectorAll('[data-product-recommendations]').forEach((section) => {
    if (!section.dataset.url) return;
    fetch(section.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const recommendations = parsed.querySelector('[data-product-recommendations]');
        if (recommendations?.innerHTML.trim()) section.innerHTML = recommendations.innerHTML;
      })
      .catch(() => {});
  });

  document.querySelectorAll('[data-preferred-sort]').forEach((section) => {
    const preferredSort = section.dataset.preferredSort;
    const currentUrl = new URL(window.location.href);
    if (!preferredSort || currentUrl.searchParams.has('sort_by')) return;
    currentUrl.searchParams.set('sort_by', preferredSort);
    window.location.replace(currentUrl.toString());
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-recommendations-more]');
    if (!button) return;
    const section = button.closest('[data-product-recommendations], .product-recommendations-preview');
    const hiddenCards = section ? [...section.querySelectorAll('.related-product-grid .product-card[hidden]')] : [];
    hiddenCards.forEach((card, index) => {
      card.hidden = false;
      card.style.animationDelay = `${Math.min(index * 55, 220)}ms`;
      card.classList.add('is-revealed');
    });
    button.setAttribute('aria-expanded', 'true');
    const status = section?.querySelector('[data-recommendations-status]');
    if (status) status.textContent = `${hiddenCards.length} more recommendations are now visible.`;
    button.hidden = true;
  });

  const initializeStoryTrays = (scope = document) => {
    scope.querySelectorAll('[data-story-tray]').forEach((tray) => {
      if (tray.dataset.storyReady === 'true') return;
      const viewer = tray.querySelector('[data-story-viewer]');
      const stage = tray.querySelector('[data-story-stage]');
      const triggers = [...tray.querySelectorAll('[data-story-open]')];
      const slides = [...tray.querySelectorAll('[data-story-slide]')];
      const progress = [...tray.querySelectorAll('[data-story-progress]')];
      const closeButton = tray.querySelector('[data-story-close]');
      const status = tray.querySelector('[data-story-status]');
      if (!viewer || !stage || !triggers.length || slides.length !== triggers.length) return;

      tray.dataset.storyReady = 'true';
      let activeIndex = 0;
      let restoreTarget = null;
      let hideTimer = null;
      let pointerStartX = null;

      const pauseMedia = () => {
        slides.forEach((slide) => {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        });
      };

      const setActiveStory = (requestedIndex) => {
        const count = slides.length;
        activeIndex = (requestedIndex + count) % count;
        pauseMedia();
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        progress.forEach((item, index) => {
          item.classList.toggle('is-current', index === activeIndex);
          item.classList.toggle('is-complete', index < activeIndex);
          item.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
        });
        const activeSlide = slides[activeIndex];
        const activeVideo = activeSlide.querySelector('video');
        if (activeVideo) activeVideo.play().catch(() => {});
        const activeHeading = activeSlide.querySelector('h3')?.textContent?.trim();
        if (status) status.textContent = `Story ${activeIndex + 1} of ${count}${activeHeading ? `: ${activeHeading}` : ''}`;
      };

      const openStories = (index) => {
        window.clearTimeout(hideTimer);
        restoreTarget = triggers[index] || triggers[0];
        viewer.hidden = false;
        setActiveStory(index);
        document.body.classList.add('story-viewer-open');
        window.requestAnimationFrame(() => viewer.classList.add('is-open'));
        window.setTimeout(() => closeButton?.focus(), 80);
      };

      const closeStories = () => {
        if (viewer.hidden) return;
        viewer.classList.remove('is-open');
        document.body.classList.remove('story-viewer-open');
        pauseMedia();
        hideTimer = window.setTimeout(() => {
          viewer.hidden = true;
          restoreTarget?.focus();
        }, 300);
      };

      triggers.forEach((trigger, index) => trigger.addEventListener('click', () => openStories(index)));
      progress.forEach((item, index) => item.addEventListener('click', () => setActiveStory(index)));
      tray.querySelectorAll('[data-story-dismiss], [data-story-close]').forEach((button) => button.addEventListener('click', closeStories));
      tray.querySelector('[data-story-prev]')?.addEventListener('click', () => setActiveStory(activeIndex - 1));
      tray.querySelector('[data-story-next]')?.addEventListener('click', () => setActiveStory(activeIndex + 1));

      stage.addEventListener('pointerdown', (event) => {
        pointerStartX = event.clientX;
      }, { passive: true });
      stage.addEventListener('pointerup', (event) => {
        if (pointerStartX === null) return;
        const distance = event.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(distance) < 48) return;
        setActiveStory(activeIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });

      viewer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeStories();
          return;
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setActiveStory(activeIndex - 1);
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setActiveStory(activeIndex + 1);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...viewer.querySelectorAll('button:not([tabindex="-1"]), a[href], video[controls]')].filter((element) => !element.closest('[aria-hidden="true"]'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  };

  initializeStoryTrays();
  document.addEventListener('shopify:section:load', (event) => initializeStoryTrays(event.target));

  document.querySelectorAll('[data-policy-content]:not([data-policy-enhanced])').forEach((content) => {
    const sourceNodes = [...content.children];
    const hasSections = sourceNodes.some((node) => node.tagName === 'H2');
    if (!hasSections) return;

    const introduction = document.createElement('div');
    const sections = [];
    let sectionBody;
    let sectionNumber = 0;
    const sectionIdPrefix = content.dataset.policyPrefix || '';
    introduction.className = 'policy-introduction rte';

    sourceNodes.forEach((node) => {
      if (node.tagName === 'H2') {
        sectionNumber += 1;
        const details = document.createElement('details');
        const summary = document.createElement('summary');
        const index = document.createElement('span');
        const title = document.createElement('h3');
        const indicator = document.createElement('i');
        sectionBody = document.createElement('div');

        details.className = 'policy-detail';
        details.open = sectionNumber === 1;
        if (sectionIdPrefix) details.id = `${sectionIdPrefix}${sectionNumber}`;
        index.textContent = String(sectionNumber).padStart(2, '0');
        title.textContent = node.textContent.trim().replace(/^\d+\.\s*/, '');
        indicator.setAttribute('aria-hidden', 'true');
        sectionBody.className = 'policy-detail-body rte';
        summary.append(index, title, indicator);
        details.append(summary, sectionBody);
        sections.push(details);
        node.remove();
        return;
      }

      if (sectionBody) sectionBody.append(node);
      else introduction.append(node);
    });

    content.replaceChildren(introduction, ...sections);
    content.dataset.policyEnhanced = 'true';
  });

  document.querySelectorAll('.legal-policy-navigator a[href^="#LegalPolicySection"]').forEach((link) => {
    link.addEventListener('click', () => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target?.matches('details')) return;
      target.parentElement?.querySelectorAll(':scope > details[open]').forEach((item) => {
        if (item !== target) item.open = false;
      });
      target.open = true;
    });
  });

  document.querySelectorAll('.product-accordions, .policy-accordion').forEach((accordion) => {
    const items = [...accordion.querySelectorAll(':scope > details')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const animateItem = (details, shouldOpen) => new Promise((resolve) => {
      const summary = details.querySelector(':scope > summary');
      const content = summary?.nextElementSibling;
      if (!summary || !content || typeof details.animate !== 'function' || reducedMotion.matches) {
        details.open = shouldOpen;
        resolve();
        return;
      }

      details.classList.add('is-animating');
      const startHeight = details.getBoundingClientRect().height;
      const collapsedHeight = summary.getBoundingClientRect().height;
      if (shouldOpen) details.open = true;
      const expandedHeight = details.scrollHeight;

      const heightAnimation = details.animate(
        [{ height: `${startHeight}px` }, { height: `${shouldOpen ? expandedHeight : collapsedHeight}px` }],
        {
          duration: shouldOpen ? 390 : 300,
          easing: shouldOpen ? 'cubic-bezier(.22,.8,.28,1)' : 'cubic-bezier(.55,.08,.35,1)',
        }
      );

      content.animate(
        shouldOpen
          ? [{ opacity: 0, transform: 'translateY(-5px)' }, { opacity: 1, transform: 'translateY(0)' }]
          : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-3px)' }],
        {
          duration: shouldOpen ? 300 : 190,
          easing: 'ease-out',
        }
      );

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (!shouldOpen) details.open = false;
        details.classList.remove('is-animating');
        resolve();
      };
      heightAnimation.addEventListener('finish', settle, { once: true });
      heightAnimation.addEventListener('cancel', settle, { once: true });
    });

    items.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (accordion.dataset.accordionBusy === 'true') return;
        accordion.dataset.accordionBusy = 'true';

        const isOpening = !details.open;
        const openSiblings = isOpening ? items.filter((item) => item !== details && item.open) : [];
        const transition = isOpening
          ? Promise.all(openSiblings.map((item) => animateItem(item, false))).then(() => animateItem(details, true))
          : animateItem(details, false);

        transition.finally(() => { delete accordion.dataset.accordionBusy; });
      });
    });
  });
})();

(() => {
  const contactForm = document.querySelector('#ContactForm');
  const topicSelect = contactForm?.querySelector('[data-contact-topic-select]');
  if (!contactForm || !topicSelect) return;

  document.querySelectorAll('[data-contact-topic]').forEach((route) => {
    route.addEventListener('click', () => {
      topicSelect.value = route.dataset.contactTopic || '';
      window.requestAnimationFrame(() => {
        contactForm.classList.remove('is-guided-to');
        window.requestAnimationFrame(() => contactForm.classList.add('is-guided-to'));
      });
    });
  });

  contactForm.addEventListener('animationend', () => {
    contactForm.classList.remove('is-guided-to');
  });
})();
